"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

export default function Footer() {
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const handleThemeChange = (e) => {
      setIsDark(!!e.detail?.isDark);
    };

    window.addEventListener("topSellersThemeChange", handleThemeChange);
    return () => window.removeEventListener("topSellersThemeChange", handleThemeChange);
  }, []);

  return (
    <footer dir="rtl" className={`w-full relative overflow-hidden transition-colors duration-[1200ms] ease-in-out ${isDark ? "bg-[#050505] text-white" : "bg-white text-gray-900"}`}>
      <div className="w-full mx-auto flex flex-col md:flex-row items-stretch min-h-[450px]">

        {/* 1. Image Section: 45% width */}
        <div className="w-full md:w-[45%] relative min-h-[350px] sm:min-h-[450px] md:min-h-[550px] p-0 m-0 pl-0 ml-0 pr-0 mr-0 shrink-0">
          <Image
            src="/footerBars.jpg"
            fill
            className="object-cover object-center"
            alt="Barsavosh Footer"
            priority
          />
        </div>

        {/* 2. Center Column: 25% width (Nav Links & Social Icons) */}
        <div className="w-full md:w-[25%] flex flex-col justify-start py-10 px-4 sm:py-12 md:py-14 shrink-0">
          {/* Links */}
          <div className={`flex flex-col items-start gap-3 font-light text-right text-sm sm:text-base transition-colors duration-[1200ms] ${isDark ? "text-gray-200" : "text-gray-800"}`}>
            <Link href="/" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>صفحه اصلی</Link>
            <Link href="/lotteryResults" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>نتایج قرعه کشی</Link>
            <Link href="/faq" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>سوالات متداول</Link>
            <Link href="/consumer" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>حمایت از حقوق مصرف کننده</Link>
            <Link href="/aboutUs" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>درباره ما</Link>
            <Link href="/products" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>فروشگاه</Link>
            <Link href="/dealers" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>نمایندگی‌ها</Link>
            <Link href="/aboutUs" className={`transition-colors duration-[1200ms] ${isDark ? "hover:text-white" : "hover:text-blue-600"}`}>تماس با ما</Link>
          </div>

          {/* Social Icons (using SVG assets - uniform height matching rectangle for YouTube) */}
          <div className="flex items-center gap-4 mt-10 md:mt-8">
            <a href="https://www.youtube.com/@barsavoshco" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center transition-opacity hover:opacity-80 shrink-0" aria-label="YouTube">
              <Image
                src="/youtubeAsset.svg"
                alt="YouTube"
                width={28}
                height={20}
                className={`h-5 w-auto object-contain transition-all duration-[1200ms] ${isDark ? "brightness-0 invert" : ""}`}
              />
            </a>
            <a href="https://www.instagram.com/barsavoshco/" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center transition-opacity hover:opacity-80 shrink-0" aria-label="Instagram">
              <Image
                src="/instagramAsset.svg"
                alt="Instagram"
                width={20}
                height={20}
                className={`h-5 w-auto object-contain transition-all duration-[1200ms] ${isDark ? "brightness-0 invert" : ""}`}
              />
            </a>
            <a href="#" className="flex items-center justify-center transition-opacity hover:opacity-80 shrink-0" aria-label="WhatsApp">
              <Image
                src="/whatsappAsset.svg"
                alt="WhatsApp"
                width={20}
                height={20}
                className={`h-5 w-auto object-contain transition-all duration-[1200ms] ${isDark ? "brightness-0 invert" : ""}`}
              />
            </a>
            <a href="https://t.me/barsavoshco" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center transition-opacity hover:opacity-80 shrink-0" aria-label="Telegram">
              <Image
                src="/telegramAsset.svg"
                alt="Telegram"
                width={22}
                height={20}
                className={`h-5 w-auto object-contain transition-all duration-[1200ms] ${isDark ? "brightness-0 invert" : ""}`}
              />
            </a>
          </div>
        </div>

        {/* 3. Left Column: 30% width (Address, Phone Numbers, Email, Badges) */}
        <div className="w-full md:w-[30%] flex flex-col justify-start py-10 px-4 sm:py-12 md:py-14 pl-6 md:pl-12 lg:pl-16 shrink-0">
          <div className={`flex flex-col gap-5 text-right transition-colors duration-[1200ms] ${isDark ? "text-gray-200" : "text-gray-800"}`}>
            {/* Address */}
            <div>
              <h4 className="font-semibold mb-1 text-sm sm:text-base">آدرس</h4>
              <p className={`font-light text-xs sm:text-sm leading-relaxed transition-colors duration-[1200ms] ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                تهران، اتوبان آزادگان جنوب، ابتدای خروجی احمدآباد مستوفی، شرکت برساوش
              </p>
            </div>

            {/* Contact Numbers */}
            <div>
              <h4 className="font-semibold mb-1 text-sm sm:text-base">شماره تماس</h4>
              <div className={`flex items-center gap-2 font-light text-xs sm:text-sm lg:text-base transition-colors duration-[1200ms] ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                <span className="latinNumbers">021-1852</span>
              </div>
            </div>

            {/* Postal Code & Email inline row */}
            <div className="flex flex-row gap-6 lg:gap-12 pt-1">
              <div>
                <h4 className="font-semibold mb-1 text-sm sm:text-base">کد پستی</h4>
                <p className={`flex items-center gap-2 font-light text-xs sm:text-sm lg:text-base transition-colors duration-[1200ms] ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  3313117797
                </p>
              </div>
              <div>
                <h4 className="font-semibold mb-1 text-sm sm:text-base">آدرس ایمیل</h4>
                <a
                  href="mailto:info@barsavosh.co"
                  className={`font-light text-xs sm:text-sm underline underline-offset-4 break-all transition-colors duration-[1200ms] ${isDark ? "text-gray-300 hover:text-white" : "text-gray-600 hover:text-black"}`}
                >
                  info@barsavosh.co
                </a>
              </div>
            </div>
          </div>

          {/* Badges (Enamad & Samandehi logos at bottom left) */}
          <div className="flex items-center gap-3 mt-8 md:mt-6 justify-start">
            <Link href="https://trustseal.enamad.ir/?id=444359&Code=7GegZpbIT29RHJg5rWbCAJ1Da1uy6JwS" target="_blank" rel="noopener noreferrer">
              <Image src="/enamad.webp" width={125} height={136} alt="enamad" className="object-contain w-19 sm:w-22 h-auto" />
            </Link>
            <Image src="/samandhi.webp" width={125} height={136} alt="samandhi" className="object-contain w-19 sm:w-22 h-auto" />
          </div>
        </div>

      </div>

      {/* 4. Bottom Centered Logo (like Porsche footer) */}
      <div className="w-full flex justify-center items-center py-10 md:py-16 mt-auto">
        <Image
          src="/Barsavoshfooter-01.svg"
          alt="Barsavosh Logo"
          width={250}
          height={40}
          className={`h-4 md:h-8 w-auto object-contain transition-all duration-[1200ms] ${isDark ? "brightness-0 invert" : ""}`}
        />
      </div>
    </footer>
  );
}